<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, watch } from 'vue';

import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import { useStaticStore } from '@/stores/useStaticStore';

import fieldStyles from '@/styles/field.module.scss';
import listItemStyles from '@/styles/listItem.module.scss';

export interface Props {
  modelValue?: string | null;
  disabled?: boolean;
  readonly?: boolean;
  label?: string;
  size?: FieldSize;
  rules?: ((v: string | null) => boolean | string)[];
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  disabled: false,
  readonly: false,
  label: 'Service Code',
  size: 'md',
  rules: () => [],
});

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();

const staticStore = useStaticStore();

const fieldValue = ref(props.modelValue);
const items = ref<openapi.GetCatalogServiceCodes_200>([]);

const menuUpdated = (): void => {
  if (props.readonly) return;
  if (!items.value.length) {
    items.value = staticStore.getServiceCodes;
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    fieldValue.value = newValue;
  },
);

watch(fieldValue, (newValue) => {
  emit('update:modelValue', newValue);
});

const sizeClass = computed(() => 'size-' + props.size);
</script>

<template>
  <v-autocomplete
    v-model="fieldValue"
    :items="items"
    :label="props.label"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :rules="props.rules"
    :class="[
      'g-v-text-field',
      $style.serviceCodeAutocompleteField,
      fieldStyles[sizeClass],
    ]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    :clearable="!props.readonly"
    :menu-icon="props.readonly ? '' : 'mdi-chevron-down'"
    auto-select-first
    item-value="code"
    item-title="code"
    data-testid="serviceCodeAutocompleteField"
    @update:menu="menuUpdated"
  >
    <template #no-data>
      <NoDataSelectVListItem />
    </template>
    <template #item="{ props: itemProps, item }">
      <v-list-item
        v-bind="itemProps"
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        :title="item?.raw?.code"
        :subtitle="item?.raw?.description"
      />
    </template>
  </v-autocomplete>
</template>

<style module lang="scss">
@use '@/styles/colors';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.serviceCodeAutocompleteField {
  :global {
    .v-field__input {
      padding-top: 0 !important;
    }
    .v-field__input input {
      margin: 0;
      padding: 0;
    }
    .v-autocomplete__selection {
      margin-top: 0;
      margin-inline-end: 6px !important;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}
</style>
